<template>
  <div
    v-if="worksheet"
    class="form-horizontal">
    <PanelTemplate
      color="info"
      :icon="IconInfo">
      {{ $t('messages.worksheetRemap') }}
    </PanelTemplate>
    <FormField
      id="selected-worksheet-preview"
      :label="$t('messages.selectedWorksheet')"
      :label-size="2"
      :field-size="10">
      <div :class="{ highlight: rangeToUpdate != null }">
        <SheetPreview
          v-if="worksheet"
          :worksheet="worksheet"
          :select-mode="rangeToUpdate != null"
          @cell-clicked="onCellClicked" />
      </div>
    </FormField>

    <template v-if="formGroup && dataRanges">
      <FormField
        v-for="column in targetColumns"
        :id="column.key"
        :key="column.key"
        :control="formGroup.controls[column.key]"
        :label="column.label"
        :label-size="2">
        <div class="range-selector">
          <SheetRange :range-from-group="formGroup.controls[column.key]"></SheetRange>
          <HSButton
            :variant="rangeToUpdate === column.key ? 'primary' : 'secondary'"
            @click="() => setRangeWithMouse(column.key)">
            {{ $t('messages.selectOnWorksheet') }}
          </HSButton>
        </div>
      </FormField>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T">
  import { Workbook, Worksheet } from 'exceljs';
  import { computed, onMounted, ref, watch } from 'vue';

  import {
    cartesianToExcel,
    excelToCartesian,
  } from '@/common/components/controls/table/Spreadsheet/ExcelCoordinatesHelper';
  import SheetPreview from '@/common/components/controls/table/Spreadsheet/SheetPreview.vue';
  import SheetRange from '@/common/components/controls/table/Spreadsheet/SheetRange.vue';
  import { DataRange, DataRanges } from '@/common/components/controls/table/Spreadsheet/SpreadsheetTypes';
  import FormControl from '@/common/components/generic-form/form-controls/FormControl';
  import FormGroup from '@/common/components/generic-form/form-controls/FormGroup';
  import DefaultValidator from '@/common/components/generic-form/form-controls/validator/DefaultValidator';
  import FormField from '@/common/components/generic-form/FormField.vue';
  import PanelTemplate from '@/common/components/templates/PanelTemplate.vue';
  import HSButton from '@/common/components/ui/Button/Button.vue';
  import { IconInfo } from '@/common/icons/monochrome';
  import EntityProperty from '@/common/models/EntityProperty';

  type DataRangesFormGroup = FormGroup<DataRanges>;

  interface IProps {
    worksheet: Worksheet;
    entityProperties: EntityProperty<T>[];
  }

  const props = defineProps<IProps>();

  interface IEmits {
    (e: 'worksheetRemapped', worksheet: Worksheet): void;
  }

  const emit = defineEmits<IEmits>();

  const rangeToUpdate = ref<string>();

  const targetColumns = ref<{ key: string; label: string }[]>([]);
  const formGroup = ref<DataRangesFormGroup>();
  const dataRanges = computed(() => formGroup.value?.value);

  const lastRow = computed(() => {
    const worksheet = props.worksheet;
    if (worksheet == null) {
      return 0;
    }
    const supposedLastRow = worksheet.lastRow.number;
    for (let rowId = supposedLastRow; rowId > 0; rowId--) {
      const row = worksheet.getRow(rowId);
      if (row.hasValues) {
        return rowId;
      }
    }
    return 0;
  });

  const remappedWorksheet = computed<Worksheet>(() => {
    const worksheet = props.worksheet;

    if (worksheet == null || dataRanges.value == null) {
      return null;
    }

    const newWorksheet = new Workbook().addWorksheet('Remapped');

    const columns = Object.fromEntries(
      Object.entries(dataRanges.value).map(([key, range]) => {
        if (range == null || range.from == null || range.to == null) {
          return [key, []];
        }
        const startCoordinates = excelToCartesian(range.from);
        const endCoordinates = excelToCartesian(range.to);
        const values = [];
        const column = startCoordinates[0];
        for (let row = startCoordinates[1]; row <= endCoordinates[1]; row++) {
          const rowValues = worksheet.getRow(row + 1).values;
          const cellValue = rowValues[column + 1];
          values.push(cellValue);
        }
        return [key, values];
      }),
    );

    const maxRows = Math.max(...Object.entries(columns).map(([, values]) => values.length));

    const headerRow = [];
    for (const key of Object.keys(columns)) {
      const entityProperty = targetColumns.value.find((entityProperty) => entityProperty.key === key);
      const label = entityProperty.label;
      headerRow.push(label);
    }
    newWorksheet.addRow(headerRow);

    for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
      const row = [];
      for (const values of Object.values(columns)) {
        row.push(values[rowIndex]);
      }
      newWorksheet.addRow(row);
    }

    return newWorksheet;
  });

  function setRangeWithMouse(entityProperty: string) {
    if (rangeToUpdate.value === entityProperty) {
      rangeToUpdate.value = null;
    } else {
      rangeToUpdate.value = entityProperty;
    }
  }

  function onCellClicked(coordinates: { row: number; column: number }) {
    if (rangeToUpdate.value != null) {
      const range = formGroup.value.controls[rangeToUpdate.value];
      if (range == null) {
        rangeToUpdate.value = null;
        return;
      }
      const from = cartesianToExcel([coordinates.column, coordinates.row]);

      const to = cartesianToExcel([coordinates.column, lastRow.value]);
      range.update({ from, to });

      rangeToUpdate.value = null;
    }
  }

  /**
   * Tries to auto-complete the ranges based on the column labels
   */
  function autoComplete() {
    for (const column of targetColumns.value) {
      const { label } = column;
      const labelTokens = label.toLowerCase().split(' ');
      const maxRowsToCheck = Math.min(lastRow.value, 10);
      const scoreMap = new Map<number, [number, number]>();
      for (let rowId = 0; rowId < maxRowsToCheck - 1; rowId++) {
        const row = props.worksheet.getRow(rowId + 1);
        for (let colId = 0; colId < props.worksheet.actualColumnCount; colId++) {
          let score = 0;
          const cell = row.getCell(colId + 1);
          if (cell == null || cell.value == null) {
            continue;
          }
          const cellValueStandardized = cell.value.toString().toLowerCase();
          for (const token of labelTokens) {
            const wordMatchRegex = new RegExp(`\\b${token}\\b`);
            if (wordMatchRegex.test(cellValueStandardized)) {
              score++;
            }
          }
          scoreMap.set(score, [rowId, colId]);
        }
      }
      if (scoreMap.size > 0) {
        // Yey we found matches
        const maxScore = Math.max(...scoreMap.keys());
        const [rowId, colId] = scoreMap.get(maxScore);
        const range = formGroup.value.controls[column.key];
        if (range == null) {
          continue;
        }
        const from = cartesianToExcel([colId, rowId + 1]);
        const to = cartesianToExcel([colId, lastRow.value - 1]);
        range.update({ from, to });
      }
    }
  }

  function rebuildFormGroup() {
    targetColumns.value = props.entityProperties.map((entityProperty) => ({
      key: entityProperty.property,
      label: entityProperty.label instanceof Function ? entityProperty.label() : entityProperty.label,
    }));
    formGroup.value = new FormGroup<DataRanges>(
      targetColumns.value.reduce((acc, column) => {
        acc[column.key] = new FormGroup<DataRange>({
          from: new FormControl<string>(null, [DefaultValidator.required]),
          to: new FormControl<string>(null, [DefaultValidator.required]),
        });
        return acc;
      }, {}),
    );
    autoComplete();
  }

  watch(
    () => props.entityProperties,
    (newEntityProperties, oldEntityProperties) => {
      if (
        newEntityProperties != null &&
        oldEntityProperties != null &&
        newEntityProperties.length === oldEntityProperties.length &&
        newEntityProperties.every((entityProperty) =>
          oldEntityProperties.some((oldEntityProperty) => oldEntityProperty.property === entityProperty.property),
        )
      ) {
        return;
      }
      rebuildFormGroup();
    },
  );

  watch(remappedWorksheet, async (worksheet) => {
    emit('worksheetRemapped', worksheet);
  });

  watch(
    () => props.worksheet,
    () => {
      formGroup.value.reset();
      rebuildFormGroup();
    },
  );

  onMounted(() => {
    rebuildFormGroup();
  });
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .columns-selector {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
  }

  .range-selector {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.2rem;

    :deep(.form-group) {
      margin-bottom: 0 !important;
    }
  }

  .highlight {
    box-shadow: 0 0.2rem 0.8rem var(--primaryColor);
  }
</style>
